import { useCallback, useEffect, useRef, useState } from "react";
import { coffreFile, coffreUpload, facturxSendPdf, type CoffreEntry, type FactureClient } from "./api/coffre";
import { downloadSignFile, fetchSignPackFile, signFetch, signUploadProgress } from "./api/client";
import { AuthGate } from "./components/AuthGate";
import { Editor } from "./pages/Editor";
import { FileRow } from "./pages/FileRow";
import { FolderGrid } from "./pages/FolderGrid";
import { LegalFrame } from "./pages/LegalFrame";
import { SubmissionList } from "./pages/SubmissionList";
import { TraceDrawer } from "./pages/TraceDrawer";
import type { SignAsk, SignDoc, SignFolder, SignParty } from "./types";
import { BrandLogo } from "./ui/BrandLogo";
import { AureliaCopyright } from "./ui/AureliaCopyright";
import { IcoFolder, IcoPlus, IcoUpload } from "./ui/icons";
import { LegalBtn } from "./ui/LegalBtn";
import { HomeToggle, type HomeTab } from "./ui/HomeToggle";
import { CloneSheet } from "./ui/CloneSheet";
import { CoffreBrowseSheet } from "./ui/CoffreBrowseSheet";
import { FactureClientSheet } from "./ui/FactureClientSheet";
import { MoveSheet } from "./ui/MoveSheet";
import { SignedOutSheet } from "./ui/SignedOutSheet";
import { PiecesSheet } from "./ui/PiecesSheet";
import { SendSheet } from "./ui/SendSheet";
import { Splash } from "./ui/Splash";
import { UploadStage } from "./ui/UploadStage";

function SignApp() {
  const [folders, setFolders] = useState<SignFolder[]>([]);
  const [inbox, setInbox] = useState<SignDoc[]>([]);
  const [archives, setArchives] = useState<SignDoc[]>([]);
  const [homeTab, setHomeTab] = useState<HomeTab>("folders");
  const [folderId, setFolderId] = useState(0);
  const [docs, setDocs] = useState<SignDoc[]>([]);
  const [name, setName] = useState("");
  const [err, setErr] = useState("");
  const [link, setLink] = useState("");
  const [open, setOpen] = useState<SignDoc | null>(null);
  const [traceId, setTraceId] = useState(0);
  const [booting, setBooting] = useState(true);
  const [docsBusy, setDocsBusy] = useState(false);
  const [inboxBusy, setInboxBusy] = useState(false);
  const [listKey, setListKey] = useState(0);
  const [hot, setHot] = useState(false);
  const dragN = useRef(0);
  const [upload, setUpload] = useState<{ name: string; pct: number; phase: "send" | "prepare" } | null>(null);
  const [sendDoc, setSendDoc] = useState<SignDoc | null>(null);
  const [sendParties, setSendParties] = useState<SignParty[]>([]);
  const [mailed, setMailed] = useState<string[]>([]);
  const [mailFailed, setMailFailed] = useState<{ email: string; error?: string | null }[]>([]);
  const [sendAsks, setSendAsks] = useState<SignAsk[]>([]);
  const [waiting, setWaiting] = useState<string[]>([]);
  const [moveDoc, setMoveDoc] = useState<SignDoc | null>(null);
  const [moveBusy, setMoveBusy] = useState(false);
  const [cloneSrc, setCloneSrc] = useState<SignDoc | null>(null);
  const [cloneTitle, setCloneTitle] = useState("");
  const [cloneBusy, setCloneBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [renameId, setRenameId] = useState(0);
  const [renameName, setRenameName] = useState("");
  const [legal, setLegal] = useState(false);
  const [piecesDoc, setPiecesDoc] = useState<SignDoc | null>(null);
  const [senderName, setSenderName] = useState("");
  const [mailingId, setMailingId] = useState(0);
  const [mailToast, setMailToast] = useState("");
  const [coffreImport, setCoffreImport] = useState(false);
  const [outDoc, setOutDoc] = useState<SignDoc | null>(null);
  const [coffreDest, setCoffreDest] = useState<SignDoc | null>(null);
  const [factureDoc, setFactureDoc] = useState<SignDoc | null>(null);
  const [outBusy, setOutBusy] = useState(false);

  const loadFolders = useCallback(async () => {
    const res = await signFetch<{ folders: SignFolder[] }>("folders.php");
    setFolders(res.folders);
  }, []);

  const loadInbox = useCallback(async () => {
    setInboxBusy(true);
    try {
      const res = await signFetch<{ documents: SignDoc[] }>("documents.php");
      setInbox(res.documents);
    } finally {
      setInboxBusy(false);
    }
  }, []);

  const loadArchives = useCallback(async () => {
    setInboxBusy(true);
    try {
      const res = await signFetch<{ documents: SignDoc[] }>("documents.php?archived=1");
      setArchives(res.documents);
    } finally {
      setInboxBusy(false);
    }
  }, []);

  const loadDocs = useCallback(async (id: number) => {
    if (!id) {
      setDocs([]);
      return;
    }
    setDocsBusy(true);
    try {
      const res = await signFetch<{ documents: SignDoc[] }>(`documents.php?folder_id=${id}`);
      setDocs(res.documents);
    } finally {
      setDocsBusy(false);
    }
  }, []);

  useEffect(() => {
    void loadFolders()
      .catch((e) => setErr(e instanceof Error ? e.message : "Dossiers."))
      .finally(() => window.setTimeout(() => setBooting(false), 420));
  }, [loadFolders]);

  useEffect(() => {
    setListKey((n) => n + 1);
    void loadDocs(folderId).catch((e) => setErr(e instanceof Error ? e.message : "Documents."));
  }, [folderId, loadDocs]);

  useEffect(() => {
    if (folderId || homeTab !== "subs") return;
    void loadInbox().catch((e) => setErr(e instanceof Error ? e.message : "Soumissions."));
  }, [folderId, homeTab, loadInbox]);

  useEffect(() => {
    if (folderId || homeTab !== "archives") return;
    void loadArchives().catch((e) => setErr(e instanceof Error ? e.message : "Archives."));
  }, [folderId, homeTab, loadArchives]);

  async function createFolder() {
    const n = name.trim();
    if (n.length < 2) return;
    const res = await signFetch<{ folder: SignFolder }>("folders.php", {
      method: "POST",
      body: JSON.stringify({ name: n }),
    });
    setName("");
    await loadFolders();
    setFolderId(res.folder.id);
  }

  async function deleteFolder(f: SignFolder) {
    if (f.doc_count > 0) {
      setErr("Dossier non vide.");
      return;
    }
    await signFetch("folders.php", { method: "DELETE", body: JSON.stringify({ id: f.id }) });
    await loadFolders();
  }

  async function onFile(file: File) {
    if (!folderId) {
      setErr("Ouvrez d’abord un dossier.");
      return;
    }
    setErr("");
    setUpload({ name: file.name, pct: 8, phase: "send" });
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder_id", String(folderId));
    fd.append("title", file.name.replace(/\.pdf$/i, ""));
    try {
      const res = await signUploadProgress<{ document: SignDoc }>("documents.php", fd, (pct) => {
        setUpload({ name: file.name, pct, phase: pct >= 100 ? "prepare" : "send" });
      });
      setUpload({ name: file.name, pct: 100, phase: "prepare" });
      await loadFolders();
      await loadDocs(folderId);
      const full = await signFetch<{ document: SignDoc }>(`documents.php?id=${res.document.id}`);
      setOpen(full.document);
    } finally {
      setUpload(null);
    }
  }

  async function openSend(d: SignDoc) {
    const full = d.parties ? d : (await signFetch<{ document: SignDoc }>(`documents.php?id=${d.id}`)).document;
    setSendDoc(full);
    setSendParties(
      (full.parties ?? []).map((p) => {
        const exempt = !!p.otp_exempt && !p.auth_mail && !p.auth_sms;
        const sms = !exempt && !!p.auth_sms && !p.auth_mail;
        return { ...p, otp_exempt: exempt, auth_sms: sms, auth_mail: !sms && !exempt };
      }),
    );
    setSendAsks(full.asks?.length ? full.asks : []);
    setSenderName((full.sender_name || "").trim());
    setLink("");
    setMailed([]);
    setMailFailed([]);
    setWaiting([]);
  }

  async function send() {
    if (!sendDoc) return;
    setSendBusy(true);
    setErr("");
    setLink("");
    setMailed([]);
    setMailFailed([]);
    try {
      const res = await signFetch<{
        document: SignDoc;
        link: string;
        mailed?: string[];
        failed?: { email: string; error?: string | null }[];
        waiting?: string[];
      }>("documents.php?action=send", {
        method: "POST",
        body: JSON.stringify({
          id: sendDoc.id,
          parties: sendParties.map((p) => ({
            id: p.id,
            email: p.email || "",
            phone: p.phone || "",
            auth_mail: !!p.auth_mail,
            auth_sms: !!p.auth_sms,
            otp_exempt: !!p.otp_exempt,
          })),
          asks: sendAsks.filter((a) => a.label.trim() !== ""),
          sender_name: senderName.trim(),
        }),
      });
      setLink(res.link);
      setMailed(res.mailed ?? []);
      setMailFailed(res.failed ?? []);
      setWaiting(res.waiting ?? []);
      if (res.document.parties) setSendParties(res.document.parties);
      await loadDocs(folderId);
      if (homeTab === "subs") await loadInbox();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Envoi.");
    } finally {
      setSendBusy(false);
    }
  }

  async function copyLink() {
    if (!link) return;
    await navigator.clipboard.writeText(link);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  async function resendMail(d: SignDoc) {
    setErr("");
    if (d.status === "draft") {
      await openSend(d);
      return;
    }
    if (mailingId) return;
    setMailingId(d.id);
    try {
      const res = await signFetch<{ mailed?: string[]; message?: string }>("documents.php?action=resend", {
        method: "POST",
        body: JSON.stringify({ id: d.id }),
      });
      setMailToast(res.message || (res.mailed?.length ? "Mail renvoyé à " + res.mailed.join(", ") + "." : "Mail renvoyé."));
      window.setTimeout(() => setMailToast(""), 3200);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Renvoi.");
    } finally {
      setMailingId(0);
    }
  }

  async function copyDoc(d: SignDoc) {
    setErr("");
    if (d.status === "draft") {
      await openSend(d);
      return;
    }
    const res = await signFetch<{ link: string }>("documents.php?action=link", {
      method: "POST",
      body: JSON.stringify({ id: d.id }),
    });
    await navigator.clipboard.writeText(res.link);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  async function openDoc(d: SignDoc) {
    const full = await signFetch<{ document: SignDoc }>(`documents.php?id=${d.id}`);
    setOpen(full.document);
  }

  async function openPieces(d: SignDoc) {
    const full = await signFetch<{ document: SignDoc }>(`documents.php?id=${d.id}`);
    setPiecesDoc(full.document);
  }

  async function archiveDoc(d: SignDoc) {
    await signFetch("documents.php", {
      method: "PATCH",
      body: JSON.stringify({ id: d.id, archive: true }),
    });
    await loadInbox();
    await loadArchives();
    await loadFolders();
    if (folderId) await loadDocs(folderId);
  }

  async function restoreDoc(d: SignDoc) {
    await signFetch("documents.php", {
      method: "PATCH",
      body: JSON.stringify({ id: d.id, restore: true }),
    });
    await loadArchives();
    await loadInbox();
    await loadFolders();
    if (folderId) await loadDocs(folderId);
  }

  async function deleteDoc(d: SignDoc) {
    await signFetch(`documents.php?id=${d.id}`, { method: "DELETE" });
    if (open?.id === d.id) setOpen(null);
    setMailToast("Brouillon supprimé.");
    window.setTimeout(() => setMailToast(""), 2800);
    await loadArchives();
    await loadInbox();
    await loadFolders();
    if (folderId) await loadDocs(folderId);
  }

  function askClone(d: SignDoc) {
    setErr("");
    setCloneSrc(d);
    setCloneTitle("");
  }

  async function confirmClone() {
    if (!cloneSrc) return;
    const title = cloneTitle.trim();
    if (title.length < 2) return;
    if (title.toLocaleLowerCase("fr") === cloneSrc.title.trim().toLocaleLowerCase("fr")) {
      setErr("Ce nom est déjà pris. Il doit s’appeler autrement.");
      return;
    }
    setCloneBusy(true);
    setErr("");
    try {
      const res = await signFetch<{ document: SignDoc }>("documents.php?action=clone", {
        method: "POST",
        body: JSON.stringify({ id: cloneSrc.id, title }),
      });
      setCloneSrc(null);
      await loadFolders();
      if (folderId) await loadDocs(folderId);
      if (homeTab === "subs") await loadInbox();
      if (res.document) setOpen(res.document);
    } finally {
      setCloneBusy(false);
    }
  }

  async function moveDocTo(folderDest: number) {
    if (!moveDoc) return;
    await signFetch("documents.php", {
      method: "PATCH",
      body: JSON.stringify({ id: moveDoc.id, folder_id: folderDest }),
    });
    setMoveDoc(null);
    await loadFolders();
    if (folderId) await loadDocs(folderId);
    if (homeTab === "subs") await loadInbox();
  }

  function askDownload(d: SignDoc) {
    if (d.status === "signed") {
      setErr("");
      setOutDoc(d);
      return;
    }
    downloadSignFile(d);
  }

  async function importFromCoffre(entry: CoffreEntry) {
    setOutBusy(true);
    try {
      const file = await coffreFile(entry.path);
      if (!file.name.toLowerCase().endsWith(".pdf")) {
        throw new Error("Choisissez un PDF.");
      }
      setCoffreImport(false);
      await onFile(file);
    } finally {
      setOutBusy(false);
    }
  }

  async function classerFacture(client: FactureClient) {
    if (!factureDoc) return;
    setOutBusy(true);
    try {
      const file = await fetchSignPackFile(factureDoc);
      await facturxSendPdf(client, file);
      setFactureDoc(null);
      setOutDoc(null);
      setMailToast(`Classé dans Factures · ${client.name}.`);
      window.setTimeout(() => setMailToast(""), 2800);
    } finally {
      setOutBusy(false);
    }
  }

  async function classerCoffre(folderPath: string) {
    if (!coffreDest) return;
    setOutBusy(true);
    try {
      const file = await fetchSignPackFile(coffreDest);
      await coffreUpload(folderPath, file);
      setCoffreDest(null);
      setOutDoc(null);
      setMailToast("Classé dans le Coffre.");
      window.setTimeout(() => setMailToast(""), 2800);
    } finally {
      setOutBusy(false);
    }
  }

  async function createFolderAndMove(folderName: string) {
    const n = folderName.trim();
    if (n.length < 2 || !moveDoc) return;
    setMoveBusy(true);
    setErr("");
    try {
      const res = await signFetch<{ folder: SignFolder }>("folders.php", {
        method: "POST",
        body: JSON.stringify({ name: n }),
      });
      await moveDocTo(res.folder.id);
    } finally {
      setMoveBusy(false);
    }
  }

  function goHome() {
    setFolderId(0);
    setDocs([]);
    setErr("");
    setRenameId(0);
    dragN.current = 0;
    setHot(false);
  }

  async function renameFolder() {
    const n = renameName.trim();
    if (!renameId || n.length < 2) return;
    await signFetch("folders.php", {
      method: "PATCH",
      body: JSON.stringify({ id: renameId, name: n }),
    });
    setRenameId(0);
    setRenameName("");
    await loadFolders();
  }

  const current = folders.find((f) => f.id === folderId);

  if (booting) return <Splash sub="Chargement de vos dossiers…" />;

  return (
    <div
      className="sg-app is-desk"
      onDragEnter={(e) => {
        if (!folderId || ![...e.dataTransfer.types].includes("Files")) return;
        e.preventDefault();
        dragN.current += 1;
        setHot(true);
      }}
      onDragOver={(e) => {
        if (!folderId || ![...e.dataTransfer.types].includes("Files")) return;
        e.preventDefault();
      }}
      onDragLeave={() => {
        if (!folderId) return;
        dragN.current = Math.max(0, dragN.current - 1);
        if (dragN.current === 0) setHot(false);
      }}
      onDrop={(e) => {
        if (!folderId) return;
        e.preventDefault();
        dragN.current = 0;
        setHot(false);
        const f = e.dataTransfer.files?.[0];
        if (f && (f.type === "application/pdf" || /\.pdf$/i.test(f.name))) {
          void onFile(f).catch((er) => setErr(er instanceof Error ? er.message : "Import."));
        } else {
          setErr("Un PDF seulement.");
        }
      }}
    >
      <header className="sg-rail">
        <div className="sg-shell sg-rail__in">
          <span className="sg-rail__grow" />
          <LegalBtn onOpen={() => setLegal(true)} />
        </div>
      </header>
      {!folderId ? (
        <div className="sg-home sg-shell" key="home">
          <BrandLogo size="lg" />
          <HomeToggle tab={homeTab} onTab={setHomeTab} />
          <p className="sg-home__lead">
            {homeTab === "folders"
              ? "Choisissez un dossier, ou créez-en un."
              : homeTab === "subs"
                ? "Tous les documents, tous dossiers confondus."
                : "Documents archivés — restaurer les remet dans leur dossier. Un brouillon peut être supprimé ici."}
          </p>
          {homeTab === "folders" ? (
            <div className="sg-create is-center">
              <input
                value={name}
                placeholder="Nouveau dossier — ex. CDI 35H"
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && void createFolder()}
              />
              <button type="button" className="sg-btn" onClick={() => void createFolder()}>
                <IcoPlus />
                Créer le dossier
              </button>
            </div>
          ) : null}
          {err && !sendDoc ? <p className="sg-err">{err}</p> : null}
          {copied ? <p className="sg-toast">Lien copié — vous pouvez l’envoyer.</p> : null}
          {mailToast ? <p className="sg-toast">{mailToast}</p> : null}
          {homeTab === "folders" ? (
            <FolderGrid
              folders={folders}
              renameId={renameId}
              renameName={renameName}
              onRenameName={setRenameName}
              onOpen={setFolderId}
              onStartRename={(f) => {
                setRenameId(f.id);
                setRenameName(f.name);
              }}
              onSaveRename={() => void renameFolder().catch((er) => setErr(er instanceof Error ? er.message : "Nom."))}
              onCancelRename={() => setRenameId(0)}
              onDelete={(f) => void deleteFolder(f).catch((er) => setErr(er instanceof Error ? er.message : "Suppression."))}
            />
          ) : homeTab === "subs" ? (
            <SubmissionList
              docs={inbox}
              busy={inboxBusy}
              onCopy={(d) => void copyDoc(d).catch((er) => setErr(er instanceof Error ? er.message : "Lien."))}
              onView={(d) => void openDoc(d).catch((er) => setErr(er instanceof Error ? er.message : "Ouverture."))}
              onArchive={(d) => void archiveDoc(d).catch((er) => setErr(er instanceof Error ? er.message : "Archive."))}
              onTrace={(d) => setTraceId(d.id)}
              onPieces={(d) => void openPieces(d).catch((er) => setErr(er instanceof Error ? er.message : "Pièces."))}
              onClone={(d) => askClone(d)}
              onMail={(d) => void resendMail(d).catch((er) => setErr(er instanceof Error ? er.message : "Renvoi."))}
              onDownload={askDownload}
              mailingId={mailingId}
            />
          ) : inboxBusy && archives.length === 0 ? (
            <p className="sg-empty is-soft">Chargement des archives…</p>
          ) : archives.length === 0 ? (
            <div className="sg-empty is-soft">
              <h2>Aucune archive</h2>
              <p>Les fichiers archivés dans un dossier apparaissent ici. Restaurer les renvoie à leur place.</p>
            </div>
          ) : (
            <div className="sg-list">
              {archives.map((d) => (
                <FileRow
                  key={d.id}
                  doc={d}
                  archived
                  onEdit={() => void openDoc(d).catch((er) => setErr(er instanceof Error ? er.message : "Ouverture."))}
                  onMove={() => {
                    setErr("");
                    setMoveDoc(d);
                  }}
                  onClone={() => askClone(d)}
                  onArchive={() => void restoreDoc(d).catch((er) => setErr(er instanceof Error ? er.message : "Restauration."))}
                  onTrace={() => setTraceId(d.id)}
                  onPieces={() => void openPieces(d).catch((er) => setErr(er instanceof Error ? er.message : "Pièces."))}
                  onMail={() => void resendMail(d).catch((er) => setErr(er instanceof Error ? er.message : "Renvoi."))}
                  onDownload={() => askDownload(d)}
                  onCopy={() => void copyDoc(d).catch((er) => setErr(er instanceof Error ? er.message : "Lien."))}
                  onDelete={() => void deleteDoc(d).catch((er) => setErr(er instanceof Error ? er.message : "Suppression."))}
                  mailing={mailingId === d.id}
                />
              ))}
            </div>
          )}
        </div>
      ) : (
        <main className="sg-main sg-shell" key={`folder-${listKey}`}>
          <div className="sg-bar is-folder">
            <BrandLogo size="sm" onClick={goHome} />
            <button type="button" className="sg-btn ghost" onClick={goHome}>
              <IcoFolder />
              Dossiers
            </button>
            <div className="sg-bar__name">
              {renameId === folderId ? (
                <input
                  className="sg-bar__input"
                  value={renameName}
                  autoFocus
                  onChange={(e) => setRenameName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") void renameFolder().catch((er) => setErr(er instanceof Error ? er.message : "Nom."));
                    if (e.key === "Escape") setRenameId(0);
                  }}
                />
              ) : (
                <h1>{current?.name}</h1>
              )}
              <button
                type="button"
                className="sg-iconbtn"
                aria-label={renameId === folderId ? "Enregistrer le nom" : "Renommer le dossier"}
                onClick={() => {
                  if (renameId === folderId) {
                    void renameFolder().catch((er) => setErr(er instanceof Error ? er.message : "Nom."));
                    return;
                  }
                  setRenameId(folderId);
                  setRenameName(current?.name || "");
                }}
              >
                {renameId === folderId ? (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M5 12.5 10 17.5 19 7.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M15.2 5.2a1.7 1.7 0 0 1 2.4 0l1.2 1.2a1.7 1.7 0 0 1 0 2.4L9.4 18.2 5 19l.8-4.4Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                  </svg>
                )}
              </button>
            </div>
            <label className="sg-btn">
              <IcoUpload />
              Importer un PDF
              <input
                type="file"
                accept="application/pdf"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = "";
                  if (f) void onFile(f).catch((er) => setErr(er instanceof Error ? er.message : "Import."));
                }}
              />
            </label>
            <button
              type="button"
              className="sg-btn ghost"
              onClick={() => {
                setErr("");
                setCoffreImport(true);
              }}
            >
              <IcoFolder />
              Importer depuis Coffre
            </button>
          </div>
          {err && !sendDoc ? <p className="sg-err sg-toast">{err}</p> : null}
          {copied ? <p className="sg-toast">Lien copié — vous pouvez l’envoyer.</p> : null}
          {mailToast ? <p className="sg-toast">{mailToast}</p> : null}
          <div className="sg-view">
            {hot ? (
              <div className="sg-drop is-hot">
                <div>
                  <strong>Lâchez — on s’en occupe</strong>
                  <span>Un PDF seulement. L’atelier s’ouvre ensuite.</span>
                </div>
              </div>
            ) : null}
            {docsBusy && docs.length === 0 ? <p className="sg-muted">Ouverture du dossier…</p> : null}
            <div className="sg-list">
              {docs.map((d) => (
                <FileRow
                  key={d.id}
                  doc={d}
                  onEdit={() => void openDoc(d).catch((er) => setErr(er instanceof Error ? er.message : "Ouverture."))}
                  onMove={() => {
                    setErr("");
                    setMoveDoc(d);
                  }}
                  onClone={() => askClone(d)}
                  onArchive={() => void archiveDoc(d).catch((er) => setErr(er instanceof Error ? er.message : "Archive."))}
                  onTrace={() => setTraceId(d.id)}
                  onPieces={() => void openPieces(d).catch((er) => setErr(er instanceof Error ? er.message : "Pièces."))}
                  onMail={() => void resendMail(d).catch((er) => setErr(er instanceof Error ? er.message : "Renvoi."))}
                  onDownload={() => askDownload(d)}
                  onCopy={() => void copyDoc(d).catch((er) => setErr(er instanceof Error ? er.message : "Lien."))}
                  mailing={mailingId === d.id}
                />
              ))}
            </div>
          </div>
        </main>
      )}

      <AureliaCopyright />

      {moveDoc ? (
        <MoveSheet
          folders={folders}
          currentId={moveDoc.folder_id}
          busy={moveBusy}
          err={err}
          onPick={(id) => void moveDocTo(id).catch((er) => setErr(er instanceof Error ? er.message : "Déplacement."))}
          onCreate={(n) => void createFolderAndMove(n).catch((er) => setErr(er instanceof Error ? er.message : "Dossier."))}
          onClose={() => {
            if (moveBusy) return;
            setMoveDoc(null);
            setErr("");
          }}
        />
      ) : null}
      {cloneSrc ? (
        <CloneSheet
          title={cloneTitle}
          sourceTitle={cloneSrc.title}
          takenNames={[...docs, ...inbox, ...archives].map((d) => d.title)}
          busy={cloneBusy}
          err={err}
          onTitle={(v) => {
            setErr("");
            setCloneTitle(v);
          }}
          onConfirm={() => void confirmClone().catch((er) => setErr(er instanceof Error ? er.message : "Clone."))}
          onClose={() => {
            if (!cloneBusy) setCloneSrc(null);
          }}
        />
      ) : null}
      {upload ? <UploadStage fileName={upload.name} progress={upload.pct} phase={upload.phase} /> : null}
      {sendDoc ? (
        <SendSheet
          doc={sendDoc}
          parties={sendParties}
          asks={sendAsks}
          link={link}
          mailed={mailed}
          failed={mailFailed}
          waiting={waiting}
          busy={sendBusy}
          err={err}
          onPartyEmail={(id, value) =>
            setSendParties((prev) => prev.map((p) => (p.id === id ? { ...p, email: value } : p)))
          }
          onPartyPhone={(id, value) =>
            setSendParties((prev) => prev.map((p) => (p.id === id ? { ...p, phone: value } : p)))
          }
          onPartyAuth={(id, key, on) =>
            setSendParties((prev) =>
              prev.map((p) => {
                if (p.id !== id) return p;
                if (key === "otp_exempt") {
                  return { ...p, otp_exempt: on, auth_mail: on ? false : true, auth_sms: false };
                }
                if (key === "auth_sms") {
                  if (!on && !p.auth_mail) return { ...p, auth_sms: false, auth_mail: true, otp_exempt: false };
                  return { ...p, auth_sms: on, auth_mail: !on, otp_exempt: false };
                }
                if (!on && !p.auth_sms) return p;
                return { ...p, auth_mail: on, auth_sms: on ? false : p.auth_sms, otp_exempt: false };
              }),
            )
          }
          onAskLabel={(i, label) => setSendAsks((prev) => prev.map((a, n) => (n === i ? { ...a, label } : a)))}
          onAskReq={(i, on) => setSendAsks((prev) => prev.map((a, n) => (n === i ? { ...a, required: on } : a)))}
          onAskAdd={() => setSendAsks((prev) => [...prev, { label: prev.length === 0 ? "Pièce d’identité" : "", required: true }])}
          onAskDel={(i) => setSendAsks((prev) => prev.filter((_, n) => n !== i))}
          senderName={senderName}
          onSenderName={setSenderName}
          onSend={() => void send()}
          onCopy={() => void copyLink()}
          onClose={() => {
            setSendDoc(null);
            setLink("");
            setMailed([]);
            setMailFailed([]);
            setWaiting([]);
          }}
        />
      ) : null}
      {open ? (
        <Editor
          doc={open}
          onClose={() => setOpen(null)}
          onSaved={(next) => {
            setOpen(next);
            void loadDocs(folderId);
            void loadFolders();
            if (homeTab === "subs") void loadInbox();
          }}
          onAskSend={(next) => {
            setOpen(next);
            void openSend(next).catch((er) => setErr(er instanceof Error ? er.message : "Envoi."));
          }}
          onTrace={() => setTraceId(open.id)}
          onDownload={() => askDownload(open)}
          onCopy={() => void copyDoc(open).catch((er) => setErr(er instanceof Error ? er.message : "Lien."))}
        />
      ) : null}
      {traceId ? <TraceDrawer id={traceId} onClose={() => setTraceId(0)} /> : null}
      {coffreImport ? (
        <CoffreBrowseSheet
          mode="file"
          title="Importer depuis le Coffre"
          busy={outBusy}
          banner={err}
          onPickFile={(e) => {
            setErr("");
            void importFromCoffre(e).catch((er) => setErr(er instanceof Error ? er.message : "Coffre."));
          }}
          onClose={() => !outBusy && setCoffreImport(false)}
        />
      ) : null}
      {outDoc && !factureDoc && !coffreDest ? (
        <SignedOutSheet
          title={outDoc.title}
          onDownload={() => {
            downloadSignFile(outDoc);
            setOutDoc(null);
          }}
          onFacture={() => {
            setErr("");
            setFactureDoc(outDoc);
          }}
          onCoffre={() => {
            setErr("");
            setCoffreDest(outDoc);
          }}
          onClose={() => setOutDoc(null)}
        />
      ) : null}
      {factureDoc ? (
        <FactureClientSheet
          filename={`${factureDoc.title}.pdf`}
          busy={outBusy}
          banner={err}
          onPick={(c) => {
            setErr("");
            void classerFacture(c).catch((er) => setErr(er instanceof Error ? er.message : "Factures."));
          }}
          onClose={() => !outBusy && setFactureDoc(null)}
        />
      ) : null}
      {coffreDest ? (
        <CoffreBrowseSheet
          mode="folder"
          title="Classer dans le Coffre"
          confirm="Classer ici"
          busy={outBusy}
          banner={err}
          onPickFolder={(p) => {
            setErr("");
            void classerCoffre(p).catch((er) => setErr(er instanceof Error ? er.message : "Coffre."));
          }}
          onClose={() => !outBusy && setCoffreDest(null)}
        />
      ) : null}
      {copied && open ? <p className="sg-toast is-float">Lien copié — vous pouvez l’envoyer.</p> : null}
      {mailToast && open ? <p className="sg-toast is-float">{mailToast}</p> : null}
      {piecesDoc ? <PiecesSheet doc={piecesDoc} onClose={() => setPiecesDoc(null)} /> : null}
      {legal ? <LegalFrame onClose={() => setLegal(false)} /> : null}
    </div>
  );
}

export default function App() {
  return <AuthGate>{() => <SignApp />}</AuthGate>;
}
